import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Search, Calculator, FileText, AlertCircle, CheckCircle, ExternalLink, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { PositionMappingService, type PositionMapping } from '@/services/PositionMappingService';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MappingTester } from '@/components/admin/MappingTester';

interface PositionMappingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMappingUpdate?: () => void;
}

export default function PositionMappingModal({ open, onOpenChange, onMappingUpdate }: PositionMappingModalProps) {
  const [mappings, setMappings] = useState<PositionMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Load mappings from database
  const loadMappings = async () => {
    try {
      setLoading(true);
      const data = await PositionMappingService.getAllMappings();
      setMappings(data);
      onMappingUpdate?.();
    } catch (error) {
      console.error('Error loading position mappings:', error);
      toast.error('Failed to load position mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadMappings();
    }
  }, [open]);

  const filteredMappings = mappings.filter(mapping => {
    const matchesSearch = mapping.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mapping.base_unit.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || mapping.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(mappings.map(m => m.category))];

  const handleRequestMapping = () => {
    toast.info('Please contact an administrator to request new position mappings');
  };

  const exportMappings = () => {
    const dataStr = JSON.stringify(mappings, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `position_mappings_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Position Mappings (View Only)
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                TradingView to cTrader position conversion ratios
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={exportMappings}>
                Export Data
              </Button>
              <Button variant="outline" size="sm" onClick={handleRequestMapping}>
                Request Mapping
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4">
          {/* Info Alert */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Position mappings are managed by administrators. Contact an admin to add or modify mappings.
              <Button variant="link" className="p-0 h-auto ml-2" asChild>
                <a href="/admin/position-mappings" target="_blank" rel="noopener noreferrer">
                  Admin Panel <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </Button>
            </AlertDescription>
          </Alert>

          {/* Search and Filter */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search symbols or base units..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Statistics */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold">{mappings.length}</div>
                <div className="text-sm text-muted-foreground">Total Mappings</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold">{categories.length}</div>
                <div className="text-sm text-muted-foreground">Categories</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold">{filteredMappings.length}</div>
                <div className="text-sm text-muted-foreground">Filtered Results</div>
              </CardContent>
            </Card>
          </div>

          {/* Mappings List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Available Position Mappings</span>
                <Badge variant="outline">{filteredMappings.length} symbols</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p>Loading position mappings...</p>
                </div>
              ) : filteredMappings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchTerm || categoryFilter !== 'all' 
                    ? 'No mappings found matching your filters'
                    : 'No position mappings available'
                  }
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredMappings.map((mapping) => (
                    <div key={mapping.id} className="border rounded-lg p-4 hover:bg-accent/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-lg">{mapping.symbol}</span>
                              <Badge variant={mapping.is_active ? 'default' : 'secondary'}>
                                {mapping.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge variant="outline">{mapping.category}</Badge>
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              Base Unit: {mapping.base_unit}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">TV → cTrader Ratio</div>
                            <div className="font-medium">{mapping.tv_to_ctrader_ratio}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">Contract Size</div>
                            <div className="font-medium">{mapping.contract_size.toLocaleString()}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mapping Tester */}
          <MappingTester />

          {/* Usage Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                How Position Mappings Work
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm">
                <p className="mb-2">Position mappings convert TradingView position sizes to cTrader lot sizes:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li><strong>TV → CT Ratio:</strong> Conversion multiplier (CT Size ÷ TV Size)</li>
                  <li><strong>Contract Size:</strong> Value of 1 cTrader lot in base units</li>
                  <li><strong>Example:</strong> TV=100, CT=0.01 → Ratio=0.0001 (0.01÷100)</li>
                  <li><strong>Formula:</strong> cTrader Lots = TV Position × Ratio</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}